import { useCallback, useEffect, useRef } from "react";
import { MIN_REGION_SIZE } from "../regionColors";
import type { Rect, Region } from "../types";

export type HandlePosition = "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";

interface DragState {
  startX: number;
  startY: number;
  initialRect: Rect;
  handle: HandlePosition | "move";
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function clampToBounds(rect: Rect): Rect {
  let { x, y, width, height } = rect;
  if (width < MIN_REGION_SIZE) width = MIN_REGION_SIZE;
  if (height < MIN_REGION_SIZE) height = MIN_REGION_SIZE;
  if (width > 100) width = 100;
  if (height > 100) height = 100;
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x + width > 100) x = 100 - width;
  if (y + height > 100) y = 100 - height;
  return { x, y, width, height };
}

function resolveOverlap(moving: Rect, fixed: Rect): Rect {
  const overlapLeft = moving.x + moving.width - fixed.x;
  const overlapRight = fixed.x + fixed.width - moving.x;
  const overlapTop = moving.y + moving.height - fixed.y;
  const overlapBottom = fixed.y + fixed.height - moving.y;

  const corrections = [
    { axis: "x" as const, value: moving.x - overlapLeft, pen: overlapLeft },
    { axis: "x" as const, value: moving.x + overlapRight, pen: overlapRight },
    { axis: "y" as const, value: moving.y - overlapTop, pen: overlapTop },
    { axis: "y" as const, value: moving.y + overlapBottom, pen: overlapBottom },
  ].filter((c) => c.pen > 0);

  if (corrections.length === 0) return moving;

  corrections.sort((a, b) => a.pen - b.pen);
  const best = corrections[0];

  const result = { ...moving };
  if (best.axis === "x") {
    result.x = best.value;
  } else {
    result.y = best.value;
  }
  return result;
}

function resolveResizeOverlap(
  moving: Rect,
  fixed: Rect,
  handle: HandlePosition,
): Rect {
  const result = { ...moving };

  if (handle.includes("e")) {
    const maxRight = fixed.x;
    if (
      moving.x < fixed.x + fixed.width &&
      moving.x + moving.width > fixed.x &&
      moving.y < fixed.y + fixed.height &&
      moving.y + moving.height > fixed.y
    ) {
      if (moving.x < fixed.x) {
        result.width = Math.min(result.width, maxRight - result.x);
      }
    }
  }
  if (handle.includes("w")) {
    const minLeft = fixed.x + fixed.width;
    if (
      moving.x < fixed.x + fixed.width &&
      moving.x + moving.width > fixed.x &&
      moving.y < fixed.y + fixed.height &&
      moving.y + moving.height > fixed.y
    ) {
      if (moving.x + moving.width > fixed.x + fixed.width) {
        const oldRight = result.x + result.width;
        result.x = Math.max(result.x, minLeft);
        result.width = oldRight - result.x;
      }
    }
  }
  if (handle.includes("s")) {
    const maxBottom = fixed.y;
    if (
      moving.x < fixed.x + fixed.width &&
      moving.x + moving.width > fixed.x &&
      moving.y < fixed.y + fixed.height &&
      moving.y + moving.height > fixed.y
    ) {
      if (moving.y < fixed.y) {
        result.height = Math.min(result.height, maxBottom - result.y);
      }
    }
  }
  if (handle.includes("n")) {
    const minTop = fixed.y + fixed.height;
    if (
      moving.x < fixed.x + fixed.width &&
      moving.x + moving.width > fixed.x &&
      moving.y < fixed.y + fixed.height &&
      moving.y + moving.height > fixed.y
    ) {
      if (moving.y + moving.height > fixed.y + fixed.height) {
        const oldBottom = result.y + result.height;
        result.y = Math.max(result.y, minTop);
        result.height = oldBottom - result.y;
      }
    }
  }

  return result;
}

function clampWithCollision(
  proposed: Rect,
  others: Rect[],
  original: Rect,
  handle: HandlePosition | "move",
): Rect {
  let result = clampToBounds(proposed);

  for (let pass = 0; pass < 3; pass++) {
    let hasOverlap = false;
    for (const other of others) {
      if (rectsOverlap(result, other)) {
        hasOverlap = true;
        if (handle === "move") {
          result = resolveOverlap(result, other);
        } else {
          result = resolveResizeOverlap(result, other, handle);
        }
        result = clampToBounds(result);
      }
    }
    if (!hasOverlap) break;
  }

  let stillOverlaps = false;
  for (const other of others) {
    if (rectsOverlap(result, other)) {
      stillOverlaps = true;
      break;
    }
  }
  if (stillOverlaps) return original;

  return result;
}

interface UseRegionDragOptions {
  region: Region;
  otherRegions: Region[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  onUpdate: (id: string, partial: Partial<Region>) => void;
}

export function useRegionDrag({
  region,
  otherRegions,
  containerRef,
  onUpdate,
}: UseRegionDragOptions) {
  const dragState = useRef<DragState | null>(null);
  const regionRef = useRef(region);
  const otherRegionsRef = useRef(otherRegions);
  const onUpdateRef = useRef(onUpdate);

  regionRef.current = region;
  otherRegionsRef.current = otherRegions;
  onUpdateRef.current = onUpdate;

  const getPercentageFromEvent = useCallback(
    (e: PointerEvent) => {
      const container = containerRef.current;
      if (!container) return { px: 0, py: 0 };
      const rect = container.getBoundingClientRect();
      return {
        px: Math.max(
          0,
          Math.min(100, ((e.clientX - rect.left) / rect.width) * 100),
        ),
        py: Math.max(
          0,
          Math.min(100, ((e.clientY - rect.top) / rect.height) * 100),
        ),
      };
    },
    [containerRef],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const state = dragState.current;
      if (!state) return;

      const { px, py } = getPercentageFromEvent(e);
      const dx = px - state.startX;
      const dy = py - state.startY;
      const { initialRect } = state;

      let proposed: Rect;

      if (state.handle === "move") {
        proposed = {
          x: initialRect.x + dx,
          y: initialRect.y + dy,
          width: initialRect.width,
          height: initialRect.height,
        };
      } else {
        proposed = { ...initialRect };
        const handle = state.handle;

        if (handle.includes("e")) {
          proposed.width = Math.max(MIN_REGION_SIZE, initialRect.width + dx);
        }
        if (handle.includes("w")) {
          const newX = initialRect.x + dx;
          const newWidth = initialRect.width - dx;
          if (newWidth >= MIN_REGION_SIZE) {
            proposed.x = newX;
            proposed.width = newWidth;
          }
        }
        if (handle.includes("s")) {
          proposed.height = Math.max(MIN_REGION_SIZE, initialRect.height + dy);
        }
        if (handle.includes("n")) {
          const newY = initialRect.y + dy;
          const newHeight = initialRect.height - dy;
          if (newHeight >= MIN_REGION_SIZE) {
            proposed.y = newY;
            proposed.height = newHeight;
          }
        }
      }

      const otherRects = otherRegionsRef.current.map((r) => ({
        x: r.x,
        y: r.y,
        width: r.width,
        height: r.height,
      }));

      const clamped = clampWithCollision(
        proposed,
        otherRects,
        initialRect,
        state.handle,
      );

      onUpdateRef.current(regionRef.current.id, {
        x: clamped.x,
        y: clamped.y,
        width: clamped.width,
        height: clamped.height,
      });
    },
    [getPercentageFromEvent],
  );

  const handlePointerUp = useCallback(() => {
    dragState.current = null;
    document.removeEventListener("pointermove", handlePointerMove);
    document.removeEventListener("pointerup", handlePointerUp);
  }, [handlePointerMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  const startDrag = useCallback(
    (e: React.PointerEvent, handle: HandlePosition | "move") => {
      if (e.ctrlKey || e.metaKey || e.button === 1) return;
      e.preventDefault();
      e.stopPropagation();

      const { px, py } = getPercentageFromEvent(e.nativeEvent);

      dragState.current = {
        startX: px,
        startY: py,
        initialRect: {
          x: regionRef.current.x,
          y: regionRef.current.y,
          width: regionRef.current.width,
          height: regionRef.current.height,
        },
        handle,
      };

      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    },
    [getPercentageFromEvent, handlePointerMove, handlePointerUp],
  );

  return { startDrag };
}
